import camelCase from "@/helpers/filters/camel_case";

function getDefaultWindow() {
  return typeof window === "undefined" ? undefined : window;
}

function resolveValue(value, vm) {
  return typeof value === "function" ? value.call(vm) : value;
}

function resolveElement(target) {
  return target && target.$el ? target.$el : target;
}

/**
 * Creates a Vue 2 mixin that observes one template ref's intersection changes.
 *
 * `root` and `rootMargin` may be getter functions when their values depend on
 * the component instance, which is the Vue 2 equivalent of reactive options.
 *
 * @param {string} refKey
 * @param {{
 *   root?: Element | Document | import("vue").default | (() => Element | Document | import("vue").default | null),
 *   immediate?: boolean,
 *   rootMargin?: string | (() => string),
 *   threshold?: number | number[],
 *   window?: Window
 * }} [options={}]
 *
 * @returns {{ isYourRefKeyVisible: boolean }} A Vue mixin object.
 */
export function isVisible(refKey, options = {}) {
  const {
    root,
    rootMargin,
    threshold = 0,
    immediate = true,
    window = getDefaultWindow(),
  } = options;
  const dataPropKey = camelCase(`$is ${refKey} visible`);

  const isSupported = Boolean(window && "IntersectionObserver" in window);

  return {
    data: () => ({
      [dataPropKey]: false,
      isVisibilityObserverActive: immediate,
      isVisibilityObserverSupported: isSupported,
    }),
    created() {
      this._$observer = null;
      this._$observedTarget = null;
      this._$observerRoot = undefined;
      this._$observerRootMargin = undefined;
      this._$observerStopped = false;
    },
    methods: {
      _$cleanupObserver() {
        if (this._$observer) this._$observer.disconnect();

        this._$observer = null;
        this._$observedTarget = null;
      },
      _$syncObserver() {
        if (
          !this.isVisibilityObserverActive ||
          !this.isVisibilityObserverSupported ||
          this._$observerStopped
        ) {
          this._$cleanupObserver();
          return;
        }

        const target = resolveElement(this.$refs[refKey]);
        const resolvedRoot = resolveElement(resolveValue(root, this));
        const resolvedRootMargin = resolveValue(rootMargin, this);

        if (!target) {
          this._$cleanupObserver();
          return;
        }

        if (
          this._$observer &&
          this._$observedTarget === target &&
          this._$observerRoot === resolvedRoot &&
          this._$observerRootMargin === resolvedRootMargin
        ) {
          return;
        }

        this._$cleanupObserver();

        const observer = new window.IntersectionObserver(
          ([entry]) => {
            this[dataPropKey] = Boolean(entry && entry.isIntersecting);
          },
          {
            root: resolvedRoot,
            rootMargin: resolvedRootMargin,
            threshold,
          }
        );

        observer.observe(target);

        this._$observer = observer;
        this._$observedTarget = target;
        this._$observerRoot = resolvedRoot;
        this._$observerRootMargin = resolvedRootMargin;
      },
      $pause() {
        this.isVisibilityObserverActive = false;
        this._$cleanupObserver();
      },
      $resume() {
        if (this._$observerStopped) return;

        this.isVisibilityObserverActive = true;
        this.$nextTick(this._$syncObserver);
      },
      $stop() {
        this.$pause();
        this._$observerStopped = true;
      },
    },
    mounted() {
      this._$syncObserver();
    },
    updated() {
      this._$syncObserver();
    },
    beforeDestroy() {
      this.$stop();
    },
  };
}
